/**
 * NodeGraph — a transition's sections as the steps they draft in, top to
 * bottom: a section's step is its longest chain of dependencies, so everything
 * in one step can draft at the same time, once the steps above are done. No
 * lines: hovering (or focusing) a section fills it, what it waits on and what
 * it feeds in their own tones (ROLE; NodeGraphLegend names them) and dims the
 * rest. Clicking one opens it.
 */
import { useState } from 'react'
import type { GraphNode, NodeEdge } from '../../types/transit'
import { cn } from '../../lib/utils'
import { nodeStatusLabel } from './status'
import StatusIcon from './StatusIcon'

// The highlighted section and its two kinds of neighbor, in the design
// system's Tag tones, so the legend under the steps can name them.
const ROLE = {
  hovered: { card: 'border-primary bg-primary', text: 'text-primary-foreground', sub: 'text-primary-foreground/80', swatch: 'bg-primary' },
  waitsOn: { card: 'border-state-accent-text/40 bg-state-accent-subtle', text: 'text-state-accent-text', sub: 'text-state-accent-text', swatch: 'bg-state-accent-subtle border border-state-accent-text/40' },
  feeds: { card: 'border-state-warning-text/40 bg-state-warning-subtle', text: 'text-state-warning-text', sub: 'text-state-warning-text', swatch: 'bg-state-warning-subtle border border-state-warning-text/40' },
}

/** Step = longest chain of dependencies (0 = no dependencies). */
function steps(nodes: GraphNode[], edges: NodeEdge[]): GraphNode[][] {
  const deps = new Map<string, string[]>()
  for (const e of edges) deps.set(e.node_key, [...(deps.get(e.node_key) ?? []), e.depends_on_key])
  const depth = new Map<string, number>()
  const depthOf = (key: string, seen = new Set<string>()): number => {
    if (depth.has(key)) return depth.get(key)!
    if (seen.has(key)) return 0 // a cycle; the backend rejects these, so draw rather than hang
    seen.add(key)
    const d = Math.max(-1, ...(deps.get(key) ?? []).map((k) => depthOf(k, seen))) + 1
    depth.set(key, d)
    return d
  }
  const out: GraphNode[][] = []
  for (const n of nodes) (out[depthOf(n.node_key)] ??= []).push(n)
  return out.filter(Boolean)
}

interface NodeGraphProps {
  nodes: GraphNode[]
  edges: NodeEdge[]
  selected: string | null
  onSelect: (nodeKey: string) => void
  /** Open info requests per node key. */
  openRequests: Map<string, number>
}

export default function NodeGraph({ nodes, edges, selected, onSelect, openRequests }: NodeGraphProps) {
  const [hover, setHover] = useState<string | null>(null)
  const waitsOn = new Set(edges.filter((e) => e.node_key === hover).map((e) => e.depends_on_key))
  const feeds = new Set(edges.filter((e) => e.depends_on_key === hover).map((e) => e.node_key))

  return (
      <ol className="flex flex-col gap-2">
        {steps(nodes, edges).map((step, i) => (
          <li key={i} className="flex gap-4 rounded-lg bg-muted/40 p-2.5">
            <div className="w-20 shrink-0 pt-2 text-sm">
              <div className="font-medium text-foreground">Step {i + 1}</div>
              {i === 0 && <div className="text-xs text-tertiary">Drafts first</div>}
            </div>
            <div className="flex flex-1 flex-wrap gap-2">
              {step.map((n) => {
                const open = openRequests.get(n.node_key) ?? 0
                const role = n.node_key === hover ? ROLE.hovered : waitsOn.has(n.node_key) ? ROLE.waitsOn : feeds.has(n.node_key) ? ROLE.feeds : null
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => onSelect(n.node_key)}
                    onMouseEnter={() => setHover(n.node_key)}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover(n.node_key)}
                    onBlur={() => setHover(null)}
                    className={cn(
                      'flex min-w-48 max-w-80 flex-col gap-1 rounded-lg border px-3 py-2 text-left transition-[opacity,background-color]',
                      role?.card ?? 'border-border bg-card',
                      selected === n.node_key && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
                      hover && !role && 'opacity-35',
                    )}
                  >
                    <span className={cn('text-sm font-medium leading-snug', role?.text ?? 'text-foreground')}>{n.name}</span>
                    <span className={cn('flex flex-wrap items-center gap-1.5 text-xs', role?.sub ?? 'text-muted-foreground')}>
                      <StatusIcon status={n.status} plain={!!role} labelled className="h-3 w-3" />
                      {nodeStatusLabel(n.status)}
                      {open > 0 && <span className={role ? undefined : 'text-destructive'}>· {open} question{open === 1 ? '' : 's'}</span>}
                    </span>
                  </button>
                )
              })}
            </div>
          </li>
        ))}
      </ol>
  )
}

/** What the highlight colors mean; shown beside the Map's title. */
export function NodeGraphLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-tertiary">
      {[
        { swatch: ROLE.hovered.swatch, label: 'Hovered section' },
        { swatch: ROLE.waitsOn.swatch, label: 'Needed first' },
        { swatch: ROLE.feeds.swatch, label: 'Comes after' },
      ].map(({ swatch, label }) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className={`h-3 w-3 rounded-[3px] ${swatch}`} aria-hidden /> {label}
        </span>
      ))}
    </div>
  )
}
